import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LoadingOutlined } from '@ant-design/icons';
import { MessageInstance } from 'antd/es/message/interface';
import { LuCheck, LuPencil } from 'react-icons/lu';
import { AppButton, Flex, InputNumeric } from '../../common';
import { useAppStore } from '@/store';
import { useProgramContext } from '@/context/ProgramContext/ProgramContext';
import {
  clampReserveQuantity,
  getBulkInputMin,
  hasValidMinCount,
} from './reserveCountLimits';
import {
  applyReserveCountDecrease,
  cancelReserveCompletely,
  reserveAbsoluteCount,
  reserveIncreaseCount,
} from './bulkReserveOperations';

type BulkPhase = 'reserve_button' | 'input' | 'display';

export interface BulkReserveControlsProps {
  meal: SelfMealEntity;
  reserve: UserWeekReserveEntity;
  selectedCount: number;
  setSelectedCount: React.Dispatch<React.SetStateAction<number>>;
  messageAPI: MessageInstance;
  locked: boolean;
  setLocked: (value: boolean) => any;
  isNewReserveMode?: boolean;
  reservable: boolean;
}

export default function BulkReserveControls({
  meal,
  reserve,
  selectedCount,
  setSelectedCount,
  messageAPI,
  locked,
  setLocked,
  isNewReserveMode = false,
  reservable,
}: BulkReserveControlsProps) {
  const { fetchCredit, apiVersion } = useAppStore();
  const { fetchCurrentSelfPrograms } = useProgramContext();
  const [t] = useTranslation(['common', 'reserve']);
  const [phase, setPhase] = useState<BulkPhase>(() => (selectedCount > 0 ? 'display' : 'reserve_button'));
  const [draftCount, setDraftCount] = useState<number | null>(() => getBulkInputMin(reserve));
  const [submitting, setSubmitting] = useState(false);

  const absoluteMax = useMemo(
    () => Math.min(reserve.validMaxCount ?? Number.MAX_SAFE_INTEGER, meal.validTotalCount ?? Number.MAX_SAFE_INTEGER),
    [reserve.validMaxCount, meal.validTotalCount],
  );

  const effectiveInputMin = selectedCount > 0 ? 0 : getBulkInputMin(reserve);

  useEffect(() => {
    if (selectedCount > 0 && phase === 'reserve_button') {
      setPhase('display');
    }
    if (selectedCount === 0 && phase === 'display') {
      setPhase('reserve_button');
      setDraftCount(getBulkInputMin(reserve));
    }
  }, [selectedCount, phase, reserve]);

  const clampDraft = useCallback((value: number | null) => {
    const fallback = selectedCount > 0 ? selectedCount : getBulkInputMin(reserve);
    const n = value == null || Number.isNaN(value) ? fallback : value;
    let clamped = clampReserveQuantity(n, effectiveInputMin, absoluteMax);
    if (clamped > 0 && hasValidMinCount(reserve) && clamped < reserve.validMinCount!) {
      clamped = reserve.validMinCount!;
    }
    return clamped;
  }, [absoluteMax, effectiveInputMin, reserve, selectedCount]);

  const openInput = useCallback((initial: number) => {
    setDraftCount(initial);
    setPhase('input');
  }, []);

  const handleConfirm = useCallback(async () => {
    if (!reservable) return;

    const target = clampDraft(draftCount);
    const current = selectedCount;

    if (target === current) {
      setPhase(current > 0 ? 'display' : 'reserve_button');
      return;
    }

    setSubmitting(true);
    setLocked(true);
    try {
      if (current === 0 && target > 0) {
        await reserveAbsoluteCount({ reserve, meal, count: target, isNewReserveMode });
        reserve.selectedCount = target;
        setSelectedCount(target);
        fetchCredit();
        await fetchCurrentSelfPrograms();
        messageAPI.success(t('reserve:messages.success-reserve'));
        setPhase('display');
        return;
      }

      if (target > current) {
        await reserveIncreaseCount({
          reserve,
          meal,
          currentCount: current,
          targetCount: target,
          isNewReserveMode,
        });
        reserve.selectedCount = target;
        setSelectedCount(target);
        fetchCredit();
        await fetchCurrentSelfPrograms();
        messageAPI.success(t('reserve:messages.success-reserve'));
        setPhase('display');
        return;
      }

      if (target < current) {
        if (target === 0) {
          await cancelReserveCompletely({ reserve, meal, selectedCount: current, apiVersion });
        } else {
          await applyReserveCountDecrease({
            reserve,
            meal,
            currentCount: current,
            targetCount: target,
            apiVersion,
            isNewReserveMode,
          });
        }
        await fetchCurrentSelfPrograms();
        fetchCredit();
        reserve.selectedCount = target;
        if (target === 0) {
          reserve.id = undefined;
          reserve.idAndConsumption = undefined;
        }
        setSelectedCount(target);
        messageAPI.success(
          target === 0
            ? t('reserve:messages.success-action')
            : t('reserve:messages.success-reserve'),
        );
        setPhase(target === 0 ? 'reserve_button' : 'display');
        if (target === 0) {
          setDraftCount(getBulkInputMin(reserve));
        }
        return;
      }
    } catch (e) {
      console.error(e);
      messageAPI.error(t('common:messages.operation-failed'));
    } finally {
      setSubmitting(false);
      setLocked(false);
    }
  }, [
    absoluteMax,
    apiVersion,
    clampDraft,
    draftCount,
    fetchCredit,
    fetchCurrentSelfPrograms,
    isNewReserveMode,
    meal,
    messageAPI,
    reserve,
    reservable,
    selectedCount,
    setLocked,
    setSelectedCount,
    t,
  ]);

  if (!reservable) {
    return null;
  }

  if (phase === 'reserve_button') {
    return (
      <Flex align="center" justify="center" className="w-full">
        <AppButton
          size="small"
          appVariant="glass"
          className="button-reserve"
          onClick={() => openInput(getBulkInputMin(reserve))}
          disabled={locked || submitting}
        >
          {t('reserve:labels.reserve')}
        </AppButton>
      </Flex>
    );
  }

  if (phase === 'display') {
    return (
      <Flex align="center" justify="center" gap={4} className="w-full reserve-bulk-display">
        <LuPencil
          role="button"
          aria-label={t('common:labels.edit')}
          className="reserve-action text-lg shrink-0"
          onClick={() => !locked && !submitting && openInput(selectedCount)}
        />
        <span className="reserve-selected-count">
          {selectedCount} {t('common:labels.no-')}
        </span>
      </Flex>
    );
  }

  return (
    <Flex align="center" justify="center" gap={3} className="w-full reserve-bulk-input-row">
      <AppButton
        appVariant="glass"
        className="reserve-act-btn reserve-inc !min-w-[1.875rem]"
        onClick={handleConfirm}
        disabled={locked || draftCount == null}
        aria-label={t('common:labels.confirm')}
      >
        {submitting ?
          <LoadingOutlined style={{ fontSize: '16px' }} spin /> :
          <LuCheck className="text-xl" />
        }
      </AppButton>
      <InputNumeric
        min={effectiveInputMin}
        max={absoluteMax}
        value={draftCount}
        onChange={(value) => {
          if (value === null) {
            setDraftCount(null);
            return;
          }
          const n = typeof value === 'string' ? Number(value) : value;
          setDraftCount(Number.isNaN(n) ? null : n);
        }}
        onBlur={() => setDraftCount((c) => clampDraft(c))}
        onPressEnter={() => {
          if (locked || submitting || draftCount == null) return;
          void handleConfirm();
        }}
        disabled={locked || submitting}
        className="!w-20 ltr reserve-bulk-qty-input flex-1 max-w-[5rem] !text-center"
      />
    </Flex>
  );
}
